import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

import { VisitorChatbotComponent } from '../visitor-chatbot/visitor-chatbot.component';
import { VisitorChatbotButtonComponent } from '../visitor-chatbot-button/visitor-chatbot-button.component';
import { VisitorChatbotService } from '../../services/visitor-chatbot.service';
import { VISITOR_CHATBOT_CONFIG } from '../../configs/visitor-chatbot.config';

@Component({
  selector: 'app-visitor-chatbot-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    VisitorChatbotComponent,
    VisitorChatbotButtonComponent
  ],
  template: `
    <div class="visitor-chatbot-wrapper">
      <!-- Backdrop Overlay -->
      <div
        *ngIf="isVisible && !isMinimized"
        class="chatbot-backdrop"
        [class.fullscreen]="isFullscreen"
        (click)="hideChat()">
      </div>

      <!-- Floating Button -->
      <app-visitor-chatbot-button 
        *ngIf="showFloatingButton"
        [customBackgroundColor]="customBackgroundColor">
      </app-visitor-chatbot-button>

      <!-- Chat Interface -->
      <app-visitor-chatbot
        *ngIf="isVisible"
        [config]="chatbotConfig"
        (closeDialog)="hideChat()"
        (minimize)="minimizeChat()"
        (fullscreen)="toggleFullscreen()">
      </app-visitor-chatbot>
    </div>
  `,
  styles: [`
    .visitor-chatbot-wrapper {
      position: relative;
      z-index: 1000001; /* Higher than button to ensure proper layering */
    }

    .chatbot-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 1000002; /* Higher than header to ensure backdrop appears above navigation */
      transition: opacity 0.3s ease;
      backdrop-filter: blur(2px);
      cursor: pointer;
      pointer-events: auto;
    }

    /* Fullscreen mode backdrop adjustments */
    .chatbot-backdrop.fullscreen {
      background-color: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(3px);
      z-index: 1000002; /* Keep same high z-index for fullscreen mode */
    }

    /* Ensure backdrop works properly in all viewport sizes */
    @media (max-width: 768px) {
      .chatbot-backdrop {
        background-color: rgba(0, 0, 0, 0.6); /* Slightly darker on mobile */
      }

      .chatbot-backdrop.fullscreen {
        background-color: rgba(0, 0, 0, 0.8); /* Even darker in fullscreen on mobile */
      }
    }
  `]
})
export class VisitorChatbotWrapperComponent implements OnInit, OnDestroy, OnChanges {
  // When true, disables question/conversation limits (used on Support page)
  @Input() unlimitedUsage = false;
  @Input() customBackgroundColor?: string;
  @Input() showFloatingButton = true; // New input property to control button visibility

  isVisible = false;
  isMinimized = false;
  isFullscreen = false;
  chatbotConfig = VISITOR_CHATBOT_CONFIG;

  private subscriptions = new Subscription();

  constructor(private chatbotService: VisitorChatbotService) { }

  ngOnInit(): void {
    // Apply limits policy on init
    this.chatbotService.setLimitsDisabled(this.unlimitedUsage);
    this.subscriptions.add(
      this.chatbotService.isVisible$.subscribe(visible => {
        this.isVisible = visible;
      })
    );

    this.subscriptions.add(
      this.chatbotService.isMinimized$.subscribe(minimized => {
        this.isMinimized = minimized;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unlimitedUsage']) {
      this.chatbotService.setLimitsDisabled(!!this.unlimitedUsage);
    }
  }

  ngOnDestroy(): void {
    // Reset limits to default when wrapper is destroyed
    this.chatbotService.setLimitsDisabled(false);
    this.subscriptions.unsubscribe();
  }

  hideChat(): void {
    this.isMinimized = false;
    this.isFullscreen = false;
    this.chatbotService.hide();
  }

  minimizeChat(): void {
    this.isMinimized = true;
    this.chatbotService.minimize();
  }

  toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
    // The actual fullscreen state is managed by the child component
  }
}
